import { useEffect, ReactNode } from "react";
import { Provider } from "@rollbar/react";
import Rollbar from "rollbar";
import { rollbarConfig } from "@config/rollbarConfig";
import { useAuth } from "@/features/auth";

import { RollbarContext } from "./RollbarContext";

// Create Rollbar instance
const rollbarInstance = new Rollbar(rollbarConfig);

interface RollbarProviderProps {
	children: ReactNode;
}

export function RollbarProvider({ children }: RollbarProviderProps) {
	const { user } = useAuth();

	// Update person tracking when user changes
	useEffect(() => {
		if (user) {
			rollbarInstance.configure({
				payload: {
					person: {
						id: user.id,
						email: user.email || undefined,
						username:
							user.user_metadata?.full_name ||
							user.user_metadata?.name ||
							undefined,
					},
					// Enhanced context for better debugging
					context: `User: ${user.email || user.id}`,
					custom: {
						environment: import.meta.env.VITE_ENVIRONMENT || "development",
						userAgent: navigator.userAgent,
						// Add organization/team context when multitenancy is implemented
						// organization_id: user.app_metadata?.organization_id,
					},
				},
			});
		} else {
			// Clear person data on logout
			rollbarInstance.configure({
				payload: {
					person: undefined,
					context: undefined,
					custom: {
						environment: import.meta.env.VITE_ENVIRONMENT || "development",
					},
				},
			});
		}
	}, [user]);

	return (
		<Provider instance={rollbarInstance}>
			<RollbarContext.Provider value={rollbarInstance}>
				{children}
			</RollbarContext.Provider>
		</Provider>
	);
}
